/* ==========================================================================
   Rotating circular logo — header only
   Base rotation: 20s per turn, clockwise. Scrolling adds a subtle speed boost
   that decays back to base when scrolling stops.

   Angle is accumulated manually rather than driven by a CSS @keyframes
   animation, because changing animation-duration mid-flight makes the browser
   recompute the current position and the mark visibly jumps.
   ========================================================================== */

(function () {
  'use strict';

  // Scoped to the header wrapper so a footer/drawer logo reusing the same
  // class is left alone. Targets the <img>, not the <a>, so the click area
  // stays put. Matches both logo variants (--solid and the transparent one).
  var SELECTOR = '.logo__image img';

  var SECONDS_PER_TURN = 20;
  var BASE_DEG_PER_SEC = 360 / SECONDS_PER_TURN;   // 18 deg/s

  var SCROLL_BOOST = 180;        // extra deg/s per px/s of scroll
  var MAX_BOOST_DEG_PER_SEC = 180; // ceiling — caps the effect at 2x base
  var SMOOTHING = 0.08;           // lower = slower ramp up and decay

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var els = [];
  var angle = 0;
  var boost = 0;
  var lastScrollY = window.scrollY;
  var lastTime = null;
  var rafId = null;

  function collect() {
    els = Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
    for (var i = 0; i < els.length; i++) {
      els[i].style.willChange = 'transform';
    }
  }

  function frame(now) {
    if (lastTime === null) lastTime = now;

    // Capped so a backgrounded tab or a long frame can't jump the mark.
    var dt = Math.min((now - lastTime) / 1000, 0.1);
    lastTime = now;

    // scrollY is read inside the rAF loop rather than from a scroll listener:
    // one read per frame, already batched, no extra event plumbing.
    var y = window.scrollY;
    var velocity = dt > 0 ? Math.abs(y - lastScrollY) / dt : 0;
    lastScrollY = y;

    var target = Math.min(velocity * SCROLL_BOOST, MAX_BOOST_DEG_PER_SEC);
    boost += (target - boost) * SMOOTHING;

    angle = (angle + (BASE_DEG_PER_SEC + boost) * dt) % 360;

    var transform = 'rotate(' + angle.toFixed(2) + 'deg)';
    for (var i = 0; i < els.length; i++) {
      els[i].style.transform = transform;
    }

    rafId = window.requestAnimationFrame(frame);
  }

  function pause() {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function start() {
    if (reduceMotion.matches || rafId !== null) return;
    collect();
    if (!els.length) return;
    lastScrollY = window.scrollY;
    lastTime = null;
    rafId = window.requestAnimationFrame(frame);
  }

  function stop() {
    pause();
    for (var i = 0; i < els.length; i++) {
      els[i].style.transform = '';
      els[i].style.willChange = '';
    }
  }

  // Don't burn frames on a tab nobody is looking at.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      pause();
    } else if (!reduceMotion.matches && els.length && rafId === null) {
      lastTime = null;
      lastScrollY = window.scrollY;
      rafId = window.requestAnimationFrame(frame);
    }
  });

  // Respond live if the OS setting changes mid-session.
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function (event) {
      if (event.matches) stop();
      else start();
    });
  }

  // The theme editor re-renders sections in place, which throws away the
  // nodes we were holding references to.
  document.addEventListener('shopify:section:load', function () {
    stop();
    start();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ==========================================================================
   Inline weight control — [square brackets]
   APPEND THIS TO assets/custom-script.js (keep the rotating logo IIFE above).
 
   Mirrors the theme's own {curly brace} system, which switches family via
   <span class="styled">. This adds a second, independent axis for weight:
 
     word        -> inherits the heading's slot font, regular
     [word]      -> same family at 700
     {word}      -> Serif Gothic (theme's own parser)
     {[word]}    -> Serif Gothic at 700
 
   The last one needs no special handling: the theme renders the family span
   server-side, and this parser finds the brackets in the text node inside it.
   Family is inherited, weight is set locally — the two compose.
 
   NOTE: only {[word]} nests, not [{word}]. In the latter the theme's span
   splits the brackets across separate text nodes and this parser won't see a
   matching pair.
   ========================================================================== */
 
(function () {
  'use strict';
 
  // Where bracket parsing is allowed. Deliberately narrow: running this over
  // the whole page would eat legitimate brackets in product descriptions,
  // reviews and rich text. Extend as needed.
  var SCOPE = 'h1, h2, h3, h4, h5, h6, [class*="heading_"]';
 
  var HEAVY_CLASS = 'cs-heavy';
  var PATTERN = /\[([^\[\]]+)\]/g;
 
  function wrapMatches(node) {
    var text = node.nodeValue;
    var frag = document.createDocumentFragment();
    var lastIndex = 0;
    var match;
 
    PATTERN.lastIndex = 0;
 
    while ((match = PATTERN.exec(text)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      var span = document.createElement('span');
      span.className = HEAVY_CLASS;
      span.textContent = match[1];
      frag.appendChild(span);
      lastIndex = PATTERN.lastIndex;
    }
 
    if (!lastIndex) return; // no complete pair — leave the node untouched
 
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    node.parentNode.replaceChild(frag, node);
  }
 
  function process(root) {
    var elements = root.querySelectorAll(SCOPE);
 
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el.dataset.csHeavyParsed) continue;
      el.dataset.csHeavyParsed = '1';
 
      // Collect first, mutate after — replacing nodes mid-walk invalidates
      // the TreeWalker's position.
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var targets = [];
      var node;
 
      while ((node = walker.nextNode())) {
        if (node.nodeValue.indexOf('[') !== -1 && node.nodeValue.indexOf(']') !== -1) {
          targets.push(node);
        }
      }
 
      for (var j = 0; j < targets.length; j++) {
        wrapMatches(targets[j]);
      }
    }
  }
 
  // The theme editor re-renders sections, bringing back unparsed brackets.
  document.addEventListener('shopify:section:load', function (event) {
    process(event.target || document);
  });
 
  // This file is loaded with defer, so parsing is already complete by the time
  // it executes. Run immediately rather than waiting for DOMContentLoaded —
  // it narrows the window in which raw brackets could be painted.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { process(document); });
  } else {
    process(document);
  }
})();

/* ---- Rotating favicon: lemon <-> blackcurrant ---- */
(function () {
  var FRAME_MS = 2200;

  var frames = window.SODA_FAVICON_FRAMES;
  if (!frames || frames.length < 2) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Preload so the swap never shows a gap
  frames.forEach(function (src) { new Image().src = src; });

  var index = 0;
  var timer = null;

  function paint() {
    // Remove every existing icon link, then append a fresh node.
    // Chrome is unreliable about repainting a mutated href.
    document.querySelectorAll('link[rel~="icon"]').forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    var link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = frames[index];
    document.head.appendChild(link);
  }

  function tick() {
    index = (index + 1) % frames.length;
    paint();
  }

  function start() { if (!timer) timer = setInterval(tick, FRAME_MS); }
  function stop()  { clearInterval(timer); timer = null; }

  document.addEventListener('visibilitychange', function () {
    document.hidden ? stop() : start();
  });

  start();
})();

/* Horizontal banners — scroll-driven pinning on mobile
   Patches BannerHScroll (global.js) from outside; see maintenance doc §11. */
(function () {
  'use strict';

  var MOBILE_MAX = 768;       // matches base.css and the section's own breakpoint
  var MOBILE_SMOOTH = 0.35;   // theme default 0.2 — see notes

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.customElements) return;

  customElements.whenDefined('banner-hscroll').then(function () {
    var proto = customElements.get('banner-hscroll').prototype;
    var isMobile = function () { return window.innerWidth <= MOBILE_MAX; };

    // 1. Never disable by width. This is the entire gate.
    proto.updateEnabledByWidth = function () {
      this.enable();
      this._scheduleScrollDownUpdate();
    };

    // 2. Drop the fallback class — all mobile overrides in base.css hang off it.
    var _enable = proto.enable;
    proto.enable = function () {
      if (this.root) this.root.classList.remove('mobile-native-scroll');
      if (isMobile()) this.smooth = MOBILE_SMOOTH;
      return _enable.call(this);
    };

    var _disable = proto.disable;
    proto.disable = function () {
      if (this.root) this.root.classList.add('mobile-native-scroll');
      return _disable.call(this);
    };

    // 3. Swipe-hint keyframe animates transform — would fight the pin.
    proto._setupBlocksAnimateOnMobile = function () {};
    proto._addBlocksAnimateOnce = function () {};

    // 4. Touch-drag and scroll both write this.x; on a phone they desync.
    var _onTouchStart = proto.onTouchStart;
    proto.onTouchStart = function (e) {
      if (isMobile()) return;
      return _onTouchStart.call(this, e);
    };

    // 5. Re-run instances that already evaluated the old gate.
    document.querySelectorAll('banner-hscroll').forEach(function (el) {
      if (!el.root || !el.active) return;
      el._setActive(false);
      el._setActive(true);
    });
  });
})();